import { useState, useMemo, Fragment } from 'react'
import { useTable, usePagination } from 'react-table'

import Button from 'Design/components/Button'
import Label from 'Design/components/Label'
import Heading from 'Design/components/Heading'
import Table, { TableBody, TableHead, TableRow, Th } from 'Design/components/Table'

import { useGetAPITokensQuery } from '../rest-api.service'

import CreateToken from './CreateToken'
import EditToken from './EditToken'

import * as date from 'Utils/date'

import styles from './APIToken.module.css'

import AddIcon from 'Icons/add.svg'

import * as Cells from './Cells'
import * as Rows from './APIRow'

const APITokenTable = (props) => {
  const { tokens = [], onEditToken } = props

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name'
      },
      {
        id: 'token',
        Header: 'API Token',
        accessor: (row) => row,
        Cell: Cells.TokenActions
      },
      {
        id: 'created_by',
        Header: 'Created By',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const createdAt = date.formatRelativeDate(value.created_at, 7)
          return (
            <Fragment>
              <div>{value.createdByUser.name}</div>
              <div className={styles.CreatedAt}>{createdAt}</div>
            </Fragment>
          )
        }
      }
    ]
  }, [])

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: tokens,
      manualPagination: true,
      pageCount: 0
    },
    usePagination
  )

  return (
    <Table {...getTableProps()}>
      <TableHead>
        {headerGroups.map((headerGroup) => {
          const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps()

          return (
            <TableRow key={key} {...headerGroupProps}>
              {headerGroup.headers.map((column) => {
                const { key, ...headerProps } = column.getHeaderProps()

                return (
                  <Th key={key} {...headerProps}>
                    {column.render('Header')}
                  </Th>
                )
              })}
              <Th isOption />
            </TableRow>
          )
        })}
      </TableHead>

      <TableBody {...getTableBodyProps()}>
        {rows.map((row) => {
          prepareRow(row)
          const { key, ...rowProps } = row.getRowProps()
          return <Rows.APIRow key={key} row={row} rowProps={rowProps} onEditToken={onEditToken} />
        })}
      </TableBody>
    </Table>
  )
}

const APIToken = () => {
  const { data = {} } = useGetAPITokensQuery()
  const [showAddTokenModal, setShowAddTokenModal] = useState(false)
  const [showEditTokenModal, setShowEditTokenModal] = useState(false)
  const [activeTokenId, setActiveTokenId] = useState(null)

  const handleAddTokenModal = () => {
    setShowAddTokenModal(true)
  }

  const hideAddTokenModal = () => {
    setShowAddTokenModal(false)
  }

  const handleEditTokenModal = (id) => {
    setActiveTokenId(id)
    setShowEditTokenModal(true)
  }

  const hideEditTokenModal = (id) => {
    setShowEditTokenModal(false)
  }

  const activeTokenData = data?.results?.find((result) => result.id === activeTokenId) || {}

  return (
    <Fragment>
      <CreateToken isOpen={showAddTokenModal} hide={hideAddTokenModal} />
      {showEditTokenModal && (
        <EditToken isOpen={showEditTokenModal} hide={hideEditTokenModal} tokenData={activeTokenData} />
      )}
      <Heading level={2}>API Tokens</Heading>
      <section>
        <Label>
          You can generate API tokens. These tokens can be used to access your company's data, please do not publish
          them anywhere.
        </Label>
      </section>
      {data?.results?.length > 0 && (
        <APITokenTable tokens={data.results} onEditToken={(id) => handleEditTokenModal(id)} />
      )}
      <Button variant='secondary' type='button' onClick={handleAddTokenModal} className={styles.AddTokenButton}>
        Generate a new API token
        <span className={styles.AddIcon}>
          <AddIcon />
        </span>
      </Button>
    </Fragment>
  )
}

export default APIToken
